import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { api } from "../../api";
import {
  Loader2,
  Save,
  Link as LinkIcon,
  Eye,
  EyeOff,
  BarChart3,
  MousePointerClick,
  Users,
  TrendingUp,
} from "lucide-react";

interface ReferralSettings {
  enabled: boolean;
  url: string;
  title: string;
  description: string;
  buttonText: string;
}

interface ClickStats {
  period: { days: number; since: string };
  totalClicks: number;
  uniqueVisitors: number;
  clicksByDay: Array<{ date: string; count: number }>;
  clicksByMachine: Array<{ machineCode: string; count: number }>;
}

export function ReferralSettingsPage() {
  const [settings, setSettings] = useState<ReferralSettings>({
    enabled: false,
    url: "",
    title: "Play on CollectorCrypt",
    description: "Use our referral link to play the gacha machines!",
    buttonText: "Play Now",
  });
  const [stats, setStats] = useState<ClickStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch settings
        const settingsResponse = await api.admin.settings.referral.get();
        if (settingsResponse.data && "settings" in settingsResponse.data) {
          setSettings(settingsResponse.data.settings as ReferralSettings);
        }

        // Fetch stats
        const statsResponse = await api.admin.settings.referral.stats.get({
          query: { days: "30" },
        });
        if (statsResponse.data && "totalClicks" in statsResponse.data) {
          setStats(statsResponse.data as ClickStats);
        }
      } catch (err) {
        setError("Failed to load referral settings");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.admin.settings.referral.put(settings);
      if (response.data && "success" in response.data && response.data.success) {
        setSuccessMessage("Referral settings saved successfully");
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to save settings");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Referral Banner Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure the CTA banner that prompts users to use your referral link
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-destructive">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400">
          {successMessage}
        </div>
      )}

      {/* Click Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <MousePointerClick className="w-4 h-4" />
                Total Clicks (30d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalClicks}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                Unique Visitors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.uniqueVisitors}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Click Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {stats.uniqueVisitors > 0
                  ? ((stats.totalClicks / stats.uniqueVisitors) * 100).toFixed(1)
                  : 0}
                %
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Clicks by Machine */}
      {stats && stats.clicksByMachine.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Clicks by Machine
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.clicksByMachine.map((item) => (
                <div
                  key={item.machineCode}
                  className="flex items-center justify-between py-2 border-b border-border last:border-0"
                >
                  <span className="font-mono text-sm">{item.machineCode}</span>
                  <span className="font-semibold">{item.count} clicks</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5" />
            Banner Configuration
          </CardTitle>
          <CardDescription>
            Customize the referral banner displayed to users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Enable Banner</label>
              <p className="text-sm text-muted-foreground">
                Show the referral banner to all users
              </p>
            </div>
            <Button
              variant={settings.enabled ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setSettings({ ...settings, enabled: !settings.enabled })
              }
            >
              {settings.enabled ? (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Enabled
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Disabled
                </>
              )}
            </Button>
          </div>

          {/* URL Input */}
          <div>
            <label className="text-sm font-medium">Referral URL</label>
            <input
              type="url"
              value={settings.url}
              onChange={(e) =>
                setSettings({ ...settings, url: e.target.value })
              }
              placeholder="https://collectorcrypt.com/ref/..."
              className="mt-1 w-full px-3 py-2 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              The URL users will be directed to when clicking the banner
            </p>
          </div>

          {/* Title Input */}
          <div>
            <label className="text-sm font-medium">Title</label>
            <input
              type="text"
              value={settings.title}
              onChange={(e) =>
                setSettings({ ...settings, title: e.target.value })
              }
              placeholder="Play on CollectorCrypt"
              maxLength={100}
              className="mt-1 w-full px-3 py-2 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Description Input */}
          <div>
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={settings.description}
              onChange={(e) =>
                setSettings({ ...settings, description: e.target.value })
              }
              placeholder="Use our referral link to play the gacha machines!"
              maxLength={500}
              rows={2}
              className="mt-1 w-full px-3 py-2 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Button Text Input */}
          <div>
            <label className="text-sm font-medium">Button Text</label>
            <input
              type="text"
              value={settings.buttonText}
              onChange={(e) =>
                setSettings({ ...settings, buttonText: e.target.value })
              }
              placeholder="Play Now"
              maxLength={50}
              className="mt-1 w-full px-3 py-2 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Preview */}
          {settings.enabled && settings.url && (
            <div>
              <label className="text-sm font-medium mb-2 block">Preview</label>
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-amber-400">
                      {settings.title}
                    </h3>
                    <p className="text-sm text-amber-400/80 mt-1">
                      {settings.description}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-black shrink-0"
                  >
                    {settings.buttonText}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-border">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
